import { TFunction } from "next-i18next";

import { ApplicationFull } from "@/lib/prisma";

import { createEmploymentDoc } from "./docx/employmentDoc/employmentDocGenerator";
import { createEmploymentDocV2 } from "./docx/employmentDocV2Generator";
import { createEmptyRefereeLetterDoc } from "./docx/emptyRefereeLetterDocGenerator";
import { createEntrepreneurshipDoc } from "./docx/entrepreneurshipDocGenerator";
import { createEntrepreneurshipDocV2 } from "./docx/entrepreneurshipDocV2Generator";
import { createExternalProfileDoc } from "./docx/externalProfileDocGenerator";
import { createExternalProfileDocNew } from "./docx/externalProfileDocGeneratorNew";
import { createPlansDoc } from "./docx/plansDocGenerator";
import { createRefereeDoc } from "./docx/refereeDocGenerator";
import { createSelfServiceCheckListDoc } from "./docx/selfServiceCheckListDocGenerator";
import { createSelfServiceCVDoc } from "./docx/selfServiceCVDocGenerator";
import { createSelfServicePersStatemantDoc } from "./docx/selfServicePersStatementDocGenerator";
import { createSelfServiceRefereeDoc } from "./docx/selfServiceRefereeDocGenerator";
import { createProfJourneyCheckListXlsx } from "./xlsx/profJourneyCheckListXlsxGenerator";
import { createRefMapXlsx } from "./xlsx/refmapXlsxGenerator";

export { useApplicationDocList } from "./useApplicationDocList";
export { useGenerateDoc } from "./useGenerateDoc";

export enum DocExtension {
  DOCX = "docx",
  XLSX = "xlsx",
}

export enum DocType {
  EmploymentDoc = "employment-doc",
  EmploymentDocV2 = "employment-doc-v2",
  EntrepreneurshipDoc = "entrepreneurship-doc",
  EntrepreneurshipDocV2 = "entrepreneurship-doc-v2",
  ProfJourneyCheckList = "prof-journey-check-list-xlsx",
  ExternalProfileDoc = "external-profile-doc",
  ExternalProfileDocNew = "external-profile-doc-new",
  RefereeDoc = "referee-doc",
  EmptyRefereeLetterDoc = "empty-referee-letter-doc",
  SelfServiceRefereeDoc = "self-service-referee-doc",
  SelfServiceCheckListDoc = "self-service-check-list-doc",
  SelfServiceCVDoc = "self-service-cv-doc",
  SelfServicePersStatemantDoc = "self-service-per-statement-doc",
  RefMap = "ref-map",
  Plans = "plans",
}

/** Saved from n8n webhook — no local doc generator */
export const AI_REF_LETTER_DOC_TYPE = "ai-ref-letter-doc" as const;

/** Merged AI letter + empty referee briefing — created on platform after AI webhook */
export const FINAL_REF_LETTER_DOC_TYPE = "final-ref-letter" as const;

export const extensionsMap = {
  [DocExtension.DOCX]: [
    DocType.Plans,
    DocType.EmploymentDoc,
    DocType.EmploymentDocV2,
    DocType.EntrepreneurshipDoc,
    DocType.EntrepreneurshipDocV2,
    DocType.ExternalProfileDoc,
    DocType.ExternalProfileDocNew,
    DocType.RefereeDoc,
    DocType.EmptyRefereeLetterDoc,
    DocType.SelfServiceCheckListDoc,
    DocType.SelfServiceCVDoc,
    DocType.SelfServiceRefereeDoc,
    DocType.SelfServicePersStatemantDoc,
  ],
  [DocExtension.XLSX]: [DocType.RefMap, DocType.ProfJourneyCheckList],
};

export const docGeneratorMap = {
  // DOCX
  [DocType.EmploymentDoc]: createEmploymentDoc,
  [DocType.EmploymentDocV2]: createEmploymentDocV2,
  [DocType.EntrepreneurshipDoc]: createEntrepreneurshipDoc,
  [DocType.EntrepreneurshipDocV2]: createEntrepreneurshipDocV2,
  [DocType.ExternalProfileDoc]: createExternalProfileDoc,
  [DocType.ExternalProfileDocNew]: createExternalProfileDocNew,
  [DocType.RefereeDoc]: createRefereeDoc,
  [DocType.EmptyRefereeLetterDoc]: createEmptyRefereeLetterDoc,
  [DocType.SelfServiceCheckListDoc]: createSelfServiceCheckListDoc,
  [DocType.SelfServiceCVDoc]: createSelfServiceCVDoc,
  [DocType.SelfServiceRefereeDoc]: createSelfServiceRefereeDoc,
  [DocType.SelfServicePersStatemantDoc]: createSelfServicePersStatemantDoc,
  [DocType.Plans]: createPlansDoc,

  // XLSX
  [DocType.RefMap]: createRefMapXlsx,
  [DocType.ProfJourneyCheckList]: createProfJourneyCheckListXlsx,
};

export interface DocGeneratorApplicationData {
  application: ApplicationFull;
}

export type DocGenerator<T> = (
  t: TFunction,
  data: T & DocGeneratorApplicationData
) => Promise<Blob>;
